import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Props {
  src: string;
  alt: string;
  /** Bounding rect of the element that triggered the lightbox. Pass `null` to fade in centered. */
  triggerRect: DOMRect | null;
  onClose: () => void;
  /** Optional accessible label for the dialog. Defaults to `alt`. */
  ariaLabel?: string;
}

type Phase = 'enter' | 'open' | 'exit';

const TRANSITION_MS = 320;
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

export const Lightbox: React.FC<Props> = ({ src, alt, triggerRect, onClose, ariaLabel }) => {
  const [phase, setPhase] = useState<Phase>('enter');
  const [transform, setTransform] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const transformFromRect = useCallback(
    (target: DOMRect): string => {
      if (!triggerRect || target.width === 0 || target.height === 0) return '';
      const sourceCenterX = triggerRect.left + triggerRect.width / 2;
      const sourceCenterY = triggerRect.top + triggerRect.height / 2;
      const targetCenterX = target.left + target.width / 2;
      const targetCenterY = target.top + target.height / 2;
      const scale = Math.min(
        triggerRect.width / target.width,
        triggerRect.height / target.height,
      );
      return `translate(${sourceCenterX - targetCenterX}px, ${sourceCenterY - targetCenterY}px) scale(${scale})`;
    },
    [triggerRect],
  );

  // First measurement pass: image renders at its natural final size invisibly,
  // we measure, and set the transform that puts it back at the trigger's rect.
  useLayoutEffect(() => {
    if (!imgRef.current) return;
    if (!triggerRect) {
      setTransform('');
      return;
    }
    setTransform(transformFromRect(imgRef.current.getBoundingClientRect()));
  }, [triggerRect, transformFromRect]);

  // After the source-rect transform paints, schedule the transition to identity.
  useEffect(() => {
    if (phase !== 'enter' || transform === null) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('open'));
    });
    return () => cancelAnimationFrame(id);
  }, [phase, transform]);

  const handleClose = useCallback(() => {
    if (phase === 'exit') return;
    if (!imgRef.current || !triggerRect) {
      onClose();
      return;
    }
    // Re-measure current target rect (in case viewport changed) and reverse the FLIP.
    setTransform(transformFromRect(imgRef.current.getBoundingClientRect()));
    setPhase('exit');
    window.setTimeout(onClose, TRANSITION_MS);
  }, [phase, triggerRect, onClose, transformFromRect]);

  // Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const measuring = transform === null;
  const atSource = phase !== 'open';
  const appliedTransform = atSource ? transform ?? '' : '';
  const backdropOpacity = phase === 'open' ? 1 : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? alt}
      onClick={handleClose}
      className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center p-4 md:p-10"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        style={{
          opacity: backdropOpacity,
          transition: `opacity ${TRANSITION_MS}ms ${EASE}`,
        }}
      />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        className="relative max-h-full max-w-full select-none object-contain shadow-2xl"
        style={{
          transform: appliedTransform,
          transition: measuring ? 'none' : `transform ${TRANSITION_MS}ms ${EASE}`,
          transformOrigin: 'center center',
          willChange: 'transform',
          opacity: measuring ? 0 : 1,
        }}
      />
    </div>
  );
};
